import { execFileSync } from 'node:child_process';
import { statSync, writeFileSync } from 'node:fs';
import { join } from 'node:path';

/**
 * Read an image off the OS clipboard and save it to disk — the "paste a screenshot" path that
 * bracketed paste can't carry (image bytes never reach stdin as text). macOS via osascript (+ sips
 * for TIFF→PNG, since screenshots are often TIFF); Linux via wl-paste (Wayland) or xclip (X11).
 * Other platforms / missing tools / empty clipboard → null. `run`/`platform` are injectable for tests.
 */

export interface ClipboardImage { path: string; mime: string; }
/** Runs a command; for the macOS path the side effect (a written file) matters, for Linux the returned stdout Buffer does. */
export type RunFn = (cmd: string, args: string[], opts?: { maxBuffer?: number; stdio?: any }) => unknown;

// macOS: osascript/sips write the file as a side effect — discard all stdio (ImageIO prints benign
// warnings like "Error creating a JP2 color space: falling back to sRGB" to stderr; we don't want those).
const SILENT = { stdio: ['ignore', 'ignore', 'ignore'] as const };
// Linux: capture stdout (the PNG bytes), discard stderr.
const CAPTURE = { stdio: ['ignore', 'pipe', 'ignore'] as const, maxBuffer: 64 * 1024 * 1024 };

/** AppleScript double-quoted POSIX path. */
const q = (s: string): string => '"' + s.replace(/\\/g, '\\\\').replace(/"/g, '\\"') + '"';
const nonEmpty = (p: string): boolean => { try { return statSync(p).size > 0; } catch { return false; } };

/** Save the clipboard image to `dir/clip-<id>.png`. Returns null when the clipboard holds no image. */
export function grabClipboardImage(
  dir: string,
  id: string,
  run: RunFn = execFileSync,
  platform: NodeJS.Platform = process.platform,
): ClipboardImage | null {
  const png = join(dir, `clip-${id}.png`);
  if (platform === 'linux') {
    // wl-paste (Wayland) / xclip (X11) write the PNG bytes to stdout; each throws if absent or no image.
    for (const [cmd, args] of [['wl-paste', ['--type', 'image/png']], ['xclip', ['-selection', 'clipboard', '-t', 'image/png', '-o']]] as const) {
      try {
        const buf = run(cmd, [...args], CAPTURE); // spread: `as const` makes args readonly; run wants string[]
        if (Buffer.isBuffer(buf) && buf.length) { writeFileSync(png, buf); return { path: png, mime: 'image/png' }; }
      } catch { /* tool missing or no image — try the next */ }
    }
    return null;
  }
  if (platform === 'win32') {
    // Windows PowerShell + WinForms (STA): save the clipboard bitmap as PNG; exit 1 when there is none.
    // The path rides in an env var, so nothing is interpolated into the script.
    const ps = "Add-Type -AssemblyName System.Windows.Forms,System.Drawing; $i = [Windows.Forms.Clipboard]::GetImage(); if (-not $i) { exit 1 }; $i.Save($env:AGENTX_CLIP_OUT, [Drawing.Imaging.ImageFormat]::Png)";
    try {
      run('powershell.exe', ['-NoProfile', '-NonInteractive', '-STA', '-Command', ps], { ...SILENT, env: { ...process.env, AGENTX_CLIP_OUT: png }, windowsHide: true } as any);
      if (nonEmpty(png)) return { path: png, mime: 'image/png' };
    } catch { /* no image on the clipboard */ }
    return null;
  }
  if (platform !== 'darwin') return null;
  // `set d` runs first → throws cleanly (before any file is opened) if the clipboard lacks that class.
  const osa = (klass: string, out: string) =>
    `set d to (the clipboard as «class ${klass}»)\n` +
    `set f to open for access POSIX file ${q(out)} with write permission\n` +
    `try\n\twrite d to f\nend try\nclose access f`;
  try { run('osascript', ['-e', osa('PNGf', png)], SILENT); if (nonEmpty(png)) return { path: png, mime: 'image/png' }; } catch { /* no PNG on clipboard */ }
  try {
    const tiff = join(dir, `clip-${id}.tiff`);
    run('osascript', ['-e', osa('TIFF', tiff)], SILENT);
    if (nonEmpty(tiff)) { run('sips', ['-s', 'format', 'png', tiff, '--out', png], SILENT); if (nonEmpty(png)) return { path: png, mime: 'image/png' }; }
  } catch { /* no image on clipboard */ }
  return null;
}

/** Put `text` on the OS clipboard (for `/copy`). pbcopy (macOS), wl-copy/xclip (Linux).
 *  Returns false when no clipboard tool is available. */
export function copyTextToClipboard(text: string, platform: NodeJS.Platform = process.platform): boolean {
  const candidates: Array<[string, string[]]> =
    platform === 'darwin' ? [['pbcopy', []]] :
    platform === 'linux' ? [['wl-copy', []], ['xclip', ['-selection', 'clipboard']]] :
    // Windows: Set-Clipboard reading stdin as UTF-8 (clip.exe would decode it with the OEM code page).
    platform === 'win32' ? [['powershell.exe', ['-NoProfile', '-NonInteractive', '-Command', '[Console]::InputEncoding = [Text.Encoding]::UTF8; Set-Clipboard -Value ([Console]::In.ReadToEnd())']]] : [];
  for (const [cmd, args] of candidates) {
    try { execFileSync(cmd, args, { input: text, stdio: ['pipe', 'ignore', 'ignore'], ...(platform === 'win32' ? { windowsHide: true } : {}) }); return true; }
    catch { /* tool missing — try the next */ }
  }
  return false;
}
